import type { SessionCheckInInput } from '../../../api/attendance-session-types';

export type DurableScanState = 'pending' | 'unknown' | 'needs_review';
export interface ScanSummary {
  readonly key: string;
  readonly scope: string;
  readonly operationId: string;
  readonly eventId: string;
  readonly sessionId: string;
  readonly capturedAt: string;
  readonly state: DurableScanState;
  readonly failureCode?: string;
}
export interface EncryptedScan extends ScanSummary {
  readonly iv: Uint8Array<ArrayBuffer>;
  readonly ciphertext: ArrayBuffer;
}

/** Injectable persistence lets tests verify encryption without mocking Web Crypto. */
export interface ScanPersistence {
  key(scope: string, candidate?: CryptoKey): Promise<CryptoKey | undefined>;
  list(scope: string): Promise<EncryptedScan[]>;
  put(scan: EncryptedScan): Promise<void>;
  remove(key: string): Promise<void>;
}

function request<T>(value: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    value.onsuccess = () => resolve(value.result);
    value.onerror = () => reject(new Error('scanner.storageError'));
  });
}
function committed(transaction: IDBTransaction): Promise<void> {
  const result = new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = transaction.onerror = () => reject(new Error('scanner.storageError'));
  });
  // The request and transaction can fail together; observe aborts even when
  // a rejected request exits the caller before its explicit await.
  void result.catch(() => undefined);
  return result;
}

/** Only opaque metadata, non-extractable keys and encrypted captures live here.
 * No bearer/refresh token, participant name, roster or confirmed attendance cache.
 * IndexedDB/Web Crypto do not protect a compromised browser or an XSS runtime. */
export class IndexedDbScanPersistence implements ScanPersistence {
  private connection?: Promise<IDBDatabase>;
  private db(): Promise<IDBDatabase> {
    if (!this.connection) this.connection = new Promise((resolve, reject) => {
      if (!globalThis.indexedDB) { reject(new Error('scanner.storageError')); return; }
      const open = indexedDB.open('event.session-scan-outbox.v1', 1);
      let failed = false;
      open.onupgradeneeded = () => {
        open.result.createObjectStore('keys', { keyPath: 'scope' });
        open.result.createObjectStore('captures', { keyPath: 'key' }).createIndex('scope', 'scope');
      };
      open.onerror = open.onblocked = () => { failed = true; reject(new Error('scanner.storageError')); };
      open.onsuccess = () => {
        if (failed) { open.result.close(); return; }
        open.result.onversionchange = () => { open.result.close(); this.connection = undefined; };
        resolve(open.result);
      };
    });
    const connection = this.connection;
    void connection.catch(() => { if (this.connection === connection) this.connection = undefined; });
    return this.connection;
  }
  async key(scope: string, candidate?: CryptoKey): Promise<CryptoKey | undefined> {
    const db = await this.db();
    const tx = db.transaction('keys', candidate ? 'readwrite' : 'readonly');
    const done = committed(tx);
    const store = tx.objectStore('keys');
    const row = await request<{ scope: string; cryptoKey: CryptoKey } | undefined>(store.get(scope));
    if (!row && candidate) store.put({ scope, cryptoKey: candidate });
    await done;
    return row?.cryptoKey ?? candidate;
  }
  async list(scope: string): Promise<EncryptedScan[]> {
    const db = await this.db(); const tx = db.transaction('captures', 'readonly'); const done = committed(tx);
    const rows = await request<EncryptedScan[]>(tx.objectStore('captures').index('scope').getAll(scope));
    await done; return rows;
  }
  async put(scan: EncryptedScan): Promise<void> {
    const db = await this.db(); const tx = db.transaction('captures', 'readwrite'); const done = committed(tx);
    tx.objectStore('captures').put(scan); await done;
  }
  async remove(key: string): Promise<void> {
    const db = await this.db(); const tx = db.transaction('captures', 'readwrite'); const done = committed(tx);
    tx.objectStore('captures').delete(key); await done;
  }
  async hasCaptures(): Promise<boolean> {
    const db = await this.db(); const tx = db.transaction('captures', 'readonly'); const done = committed(tx);
    const [count] = await Promise.all([request(tx.objectStore('captures').count()), done]);
    return count > 0;
  }
}

const updatePersistence = new IndexedDbScanPersistence();
export async function hasPendingSessionScans(): Promise<boolean> {
  if (!globalThis.indexedDB) return false;
  return updatePersistence.hasCaptures();
}
function notifyJournal(): void { if (typeof window !== 'undefined') window.dispatchEvent(new Event('event:session-outbox-changed')); }

function binding(scan: ScanSummary): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(JSON.stringify([scan.scope, scan.operationId, scan.eventId, scan.sessionId, scan.capturedAt]));
}
function summary(scan: EncryptedScan): ScanSummary {
  const { iv: _iv, ciphertext: _ciphertext, ...metadata } = scan;
  return metadata;
}

export class SessionScanVault {
  constructor(private readonly persistence: ScanPersistence, private readonly crypto: Crypto = globalThis.crypto) {}
  async scope(tenant: string, subject: string): Promise<string> {
    if (!tenant || !subject) throw new Error('scanner.scopeRequired');
    const digest = await this.crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify([tenant, subject])));
    return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
  }
  async list(scope: string): Promise<ScanSummary[]> {
    return (await this.persistence.list(scope)).map(summary).sort((a, b) => a.capturedAt.localeCompare(b.capturedAt));
  }
  async capture(scope: string, eventId: string, sessionId: string, input: SessionCheckInInput, capturedAt: string): Promise<ScanSummary> {
    if (!scope || !eventId || !sessionId || !input.operationId || Boolean(input.token) === Boolean(input.enrollmentId) || !Number.isFinite(Date.parse(capturedAt))) throw new Error('scanner.invalidCapture');
    if ((await this.persistence.list(scope)).length >= 1000) throw new Error('scanner.queueFull');
    let key = await this.persistence.key(scope);
    if (!key) {
      const candidate = await this.crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
      key = await this.persistence.key(scope, candidate);
    }
    if (!key) throw new Error('scanner.storageError');
    const metadata: ScanSummary = { key: `${scope}:${input.operationId}`, scope, operationId: input.operationId, eventId, sessionId, capturedAt, state: 'pending' };
    if ((await this.persistence.list(scope)).some(row => row.operationId === input.operationId)) throw new Error('scanner.operationExists');
    const iv = this.crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await this.crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: binding(metadata) }, key, new TextEncoder().encode(JSON.stringify({ ...input, clientCapturedAt: capturedAt })));
    // Do not report locally saved until the transaction commits.
    await this.persistence.put({ ...metadata, iv, ciphertext });
    notifyJournal();
    return metadata;
  }
  async payload(scope: string, operationId: string): Promise<SessionCheckInInput> {
    const row = (await this.persistence.list(scope)).find(value => value.operationId === operationId);
    const key = await this.persistence.key(scope);
    if (!row || !key) throw new Error('scanner.storageError');
    const plain = await this.crypto.subtle.decrypt({ name: 'AES-GCM', iv: row.iv, additionalData: binding(row) }, key, row.ciphertext);
    const input: unknown = JSON.parse(new TextDecoder().decode(plain));
    if (!input || typeof input !== 'object' || !('operationId' in input) || input.operationId !== operationId) throw new Error('scanner.storageError');
    const token = 'token' in input && typeof input.token === 'string' && input.token.length > 0;
    const enrollment = 'enrollmentId' in input && typeof input.enrollmentId === 'string' && input.enrollmentId.length > 0;
    if (token === enrollment || !('clientCapturedAt' in input) || input.clientCapturedAt !== row.capturedAt) throw new Error('scanner.storageError');
    return input as SessionCheckInInput;
  }
  async mark(scope: string, operationId: string, state: DurableScanState, failureCode?: string): Promise<void> {
    const row = (await this.persistence.list(scope)).find(value => value.operationId === operationId);
    if (!row) return;
    await this.persistence.put({ ...row, state, failureCode });
    notifyJournal();
  }
  async acknowledge(scope: string, operationId: string): Promise<void> {
    await this.persistence.remove(`${scope}:${operationId}`);
    notifyJournal();
  }
  /** A deliberate local disposition, never an attendance mutation or an unknown-result cleanup. */
  async discardReviewed(scope: string, operationId: string): Promise<void> {
    const row = (await this.persistence.list(scope)).find(value => value.operationId === operationId);
    if (!row || row.state !== 'needs_review') throw new Error('scanner.reviewRequired');
    await this.persistence.remove(row.key);
    notifyJournal();
  }
}

export function createSessionScanVault(): SessionScanVault {
  return new SessionScanVault(new IndexedDbScanPersistence());
}
